import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, InputNumber, Switch, message, Space, Tag, Input, Select, DatePicker, Divider, Collapse, Card, Row, Col, Statistic, Grid, Popconfirm, Checkbox } from 'antd';
import { EditOutlined, SearchOutlined, FilterOutlined, ClearOutlined, HistoryOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { musterilerAPI, odemeRutinleriAPI, musteriAnlasmaFiyatiTarihceAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, paraInputProps } from '../utils/format';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';

const PAKETLEME_RENK = { 1: 'blue', 2: 'orange' };

const FIYAT_TIPI_OPTIONS = [
  { value: 'sabit', label: 'Sabit Fiyat' },
  { value: 'tutar_indirim', label: 'Tutarsal İndirim (₺)' },
  { value: 'yuzde_indirim', label: 'Yüzdesel İndirim (%)' },
];

// Anlaşma değerini fiyat tipine göre okunur biçimde gösterir.
const anlasmaGoster = (fiyat, tipi) => {
  if (fiyat == null) return '-';
  if (tipi === 'tutar_indirim') return `${formatPara(fiyat)} indirim`;
  if (tipi === 'yuzde_indirim') return `%${Number(fiyat)} indirim`;
  return formatPara(fiyat); // sabit
};

const gunler = [
  { key: 'pazartesi_kisi', label: 'Pazartesi' },
  { key: 'sali_kisi', label: 'Salı' },
  { key: 'carsamba_kisi', label: 'Çarşamba' },
  { key: 'persembe_kisi', label: 'Perşembe' },
  { key: 'cuma_kisi', label: 'Cuma' },
  { key: 'cumartesi_kisi', label: 'Cumartesi' },
  { key: 'pazar_kisi', label: 'Pazar' },
];

function Musteriler() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { paketlemeTipleri, paketlemeTipiOptions } = useLookups();
  const paketlemeAdById = Object.fromEntries(paketlemeTipleri.map(p => [p.id, p.ad]));

  const [data, setData] = useState([]);
  const [odemeRutinleri, setOdemeRutinleri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreAdIds, setFiltreAdIds] = useState([]);
  const [pasifleriGoster, setPasifleriGoster] = useState(false);
  const [filtreFaturali, setFiltreFaturali] = useState([]);
  const [filtreOdemeRutiniIds, setFiltreOdemeRutiniIds] = useState([]);
  const [filtrePaketleme, setFiltrePaketleme] = useState([]);
  const [filtreFiyatOp, setFiltreFiyatOp] = useState(null);
  const [filtreFiyatDeger, setFiltreFiyatDeger] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Anlaşma fiyatı tarihçesi modalı
  const [tarihceModalOpen, setTarihceModalOpen] = useState(false);
  const [tarihceMusteri, setTarihceMusteri] = useState(null);
  const [tarihceData, setTarihceData] = useState([]);
  const [tarihceLoading, setTarihceLoading] = useState(false);
  const [tarihceForm] = Form.useForm();
  const [tarihceEditId, setTarihceEditId] = useState(null);
  const tarihceFiyatTipi = Form.useWatch('fiyat_tipi', tarihceForm) || 'sabit';

  const tarihceAc = async (musteri) => {
    setTarihceMusteri(musteri);
    setTarihceModalOpen(true);
    setTarihceEditId(null);
    tarihceForm.resetFields();
    await tarihceYukle(musteri.id);
  };

  const tarihceYukle = async (musteriId) => {
    setTarihceLoading(true);
    try {
      const res = await musteriAnlasmaFiyatiTarihceAPI.getByMusteri(musteriId);
      setTarihceData(res.data || []);
    } catch (err) {
      message.error('Tarihçe yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setTarihceLoading(false);
    }
  };

  const tarihceKaydet = async (values) => {
    const payload = {
      musteri_id: tarihceMusteri.id,
      baslangic_tarihi: values.baslangic_tarihi.format('YYYY-MM-DD'),
      fiyat: values.fiyat,
      fiyat_tipi: values.fiyat_tipi || 'sabit',
    };
    try {
      if (tarihceEditId) {
        await musteriAnlasmaFiyatiTarihceAPI.update(tarihceEditId, payload);
        message.success('Tarihçe satırı güncellendi');
      } else {
        await musteriAnlasmaFiyatiTarihceAPI.create(payload);
        message.success('Tarihçe satırı eklendi');
      }
      tarihceForm.resetFields();
      setTarihceEditId(null);
      await tarihceYukle(tarihceMusteri.id);
      fetchData(); // tablodaki anlasma_fiyati cache'ini yenile
    } catch (err) {
      message.error('Kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const tarihceDuzenleyiBasla = (record) => {
    setTarihceEditId(record.id);
    tarihceForm.setFieldsValue({
      baslangic_tarihi: dayjs(record.baslangic_tarihi),
      fiyat: Number(record.fiyat),
      fiyat_tipi: record.fiyat_tipi || 'sabit',
    });
  };

  const tarihceSil = async (id) => {
    try {
      await musteriAnlasmaFiyatiTarihceAPI.remove(id);
      message.success('Tarihçe satırı silindi');
      await tarihceYukle(tarihceMusteri.id);
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [musteriRes, rutinRes] = await Promise.all([
        musterilerAPI.getAll(),
        odemeRutinleriAPI.getAll(),
      ]);
      setData(musteriRes.data);
      setOdemeRutinleri(rutinRes.data);
    } catch (err) {
      message.error('Müşteriler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const duzenleAd = searchParams.get('duzenle');
    if (duzenleAd && data.length > 0) {
      const musteri = data.find(d => d.ad === duzenleAd);
      if (musteri) {
        handleEdit(musteri);
        navigate('/musteriler', { replace: true });
      }
    }
  }, [data, searchParams]);

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      aktif: !!record.aktif,
      faturali: !!record.faturali,
      pazartesi_kisi: record.pazartesi_kisi || 0,
      sali_kisi: record.sali_kisi || 0,
      carsamba_kisi: record.carsamba_kisi || 0,
      persembe_kisi: record.persembe_kisi || 0,
      cuma_kisi: record.cuma_kisi || 0,
      cumartesi_kisi: record.cumartesi_kisi || 0,
      pazar_kisi: record.pazar_kisi || 0,
      odeme_rutini_id: record.odeme_rutini_id,
      vergi_dairesi: record.vergi_dairesi,
      tc_vkn: record.tc_vkn,
      unvan: record.unvan,
      tic_sic_no: record.tic_sic_no,
      yetkili_isim: record.yetkili_isim,
      yetkili_telefon: record.yetkili_telefon,
      musteri_adres: record.musteri_adres,
      aciklama: record.aciklama,
      paketleme_tipi_id: record.paketleme_tipi_id || 2,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      await musterilerAPI.update(editingRecord.id, {
        ...values,
        aktif: values.aktif ? 1 : 0,
        faturali: values.faturali ? 1 : 0,
      });
      message.success('Müşteri güncellendi');
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error('Güncellenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (aramaMetni && !(d.ad || '').toLowerCase().includes(aramaMetni.toLowerCase())) return false;
      if (filtreAdIds.length > 0 && !filtreAdIds.includes(d.id)) return false;
      if (!pasifleriGoster && d.aktif === 0) return false;
      if (!secimeUyuyor(filtreFaturali, d.faturali)) return false;
      if (!secimeUyuyor(filtreOdemeRutiniIds, d.odeme_rutini_id)) return false;
      if (filtrePaketleme.length > 0 && !filtrePaketleme.includes(d.paketleme_tipi_id || 2)) return false;
      if (filtreFiyatOp && filtreFiyatDeger != null) {
        const fiyat = Number(d.anlasma_fiyati || 0), deger = Number(filtreFiyatDeger);
        if (filtreFiyatOp === 'gt' && !(fiyat > deger)) return false;
        if (filtreFiyatOp === 'gte' && !(fiyat >= deger)) return false;
        if (filtreFiyatOp === 'lt' && !(fiyat < deger)) return false;
        if (filtreFiyatOp === 'lte' && !(fiyat <= deger)) return false;
        if (filtreFiyatOp === 'eq' && !(fiyat === deger)) return false;
      }
      return true;
    });
  }, [data, aramaMetni, filtreAdIds, pasifleriGoster, filtreFaturali, filtreOdemeRutiniIds, filtrePaketleme, filtreFiyatOp, filtreFiyatDeger]);

  const musteriAdOptions = useMemo(() =>
    [...data].sort((a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr')).map(m => ({ value: m.id, label: m.ad })),
    [data]
  );

  const faturaliFilterOptions = useMemo(
    () => bosSecenekliOptions([{ value: 1, label: 'Faturalı' }, { value: 0, label: 'Faturasız' }], data, 'faturali'),
    [data]
  );

  const odemeRutiniFilterOptions = useMemo(
    () => bosSecenekliOptions(odemeRutinleri.map(o => ({ value: o.id, label: o.ad })), data, 'odeme_rutini_id'),
    [odemeRutinleri, data]
  );

  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, toplamKisi: 0 };
    filteredData.forEach(d => {
      gunler.forEach(g => { t.toplamKisi += Number(d[g.key] || 0); });
    });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreAdIds.length > 0 || pasifleriGoster || filtreFaturali.length > 0 || filtreOdemeRutiniIds.length > 0 || filtrePaketleme.length > 0 || filtreFiyatOp;
  const filtreTemizle = () => { setFiltreAdIds([]); setPasifleriGoster(false); setFiltreFaturali([]); setFiltreOdemeRutiniIds([]); setFiltrePaketleme([]); setFiltreFiyatOp(null); setFiltreFiyatDeger(null); };

  const rutinAdi = (id) => {
    const r = odemeRutinleri.find(o => o.id === id);
    return r ? r.ad : '-';
  };

  const maxKisi = (r) => Math.max(
    Number(r.pazartesi_kisi || 0),
    Number(r.sali_kisi || 0),
    Number(r.carsamba_kisi || 0),
    Number(r.persembe_kisi || 0),
    Number(r.cuma_kisi || 0),
    Number(r.cumartesi_kisi || 0),
    Number(r.pazar_kisi || 0),
  );

  const columns = [
    { title: 'Müşteri', dataIndex: 'ad', key: 'ad', sorter: (a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr') },
    { title: 'Telefon', dataIndex: 'telefon', key: 'telefon', responsive: ['sm'] },
    { title: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2 }}>{isMobile ? 'Max\nKişi' : 'Max Kişi'}</span>, key: 'max_kisi', width: isMobile ? 50 : 80, align: 'center',
      render: (_, r) => maxKisi(r) || '-',
      sorter: (a, b) => maxKisi(a) - maxKisi(b),
    },
    { title: 'Durum', dataIndex: 'aktif', key: 'aktif', responsive: ['md'],
      render: (v) => v ? <Tag color="green">Aktif</Tag> : <Tag color="red">Pasif</Tag>,
    },
    { title: 'Fatura', dataIndex: 'faturali', key: 'faturali', responsive: ['md'],
      render: (v) => v ? <Tag color="blue">Faturalı</Tag> : <Tag>Faturasız</Tag>,
    },
    { title: 'Ödeme Rutini', dataIndex: 'odeme_rutini_id', key: 'odeme_rutini_id', width: 120, responsive: ['lg'], render: (v) => rutinAdi(v) },
    { title: 'Unvan', dataIndex: 'unvan', key: 'unvan', responsive: ['lg'] },
    { title: 'Pzt', dataIndex: 'pazartesi_kisi', key: 'pazartesi_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Sal', dataIndex: 'sali_kisi', key: 'sali_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Çar', dataIndex: 'carsamba_kisi', key: 'carsamba_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Per', dataIndex: 'persembe_kisi', key: 'persembe_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Cum', dataIndex: 'cuma_kisi', key: 'cuma_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Cmt', dataIndex: 'cumartesi_kisi', key: 'cumartesi_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Paz', dataIndex: 'pazar_kisi', key: 'pazar_kisi', width: 55, align: 'center', responsive: ['lg'] },
    { title: 'Paketleme', dataIndex: 'paketleme_tipi_id', key: 'paketleme_tipi_id', width: 110, align: 'center', responsive: ['md'],
      render: (id) => <Tag color={PAKETLEME_RENK[id] || 'default'}>{paketlemeAdById[id] || '-'}</Tag>,
    },
    { title: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2 }}>{isMobile ? 'Anlaşma\nFiyatı' : 'Anlaşma Fiyatı'}</span>, dataIndex: 'anlasma_fiyati', key: 'anlasma_fiyati', width: isMobile ? 90 : 140, align: 'right',
      render: (v, r) => v != null ? anlasmaGoster(v, r.anlasma_fiyat_tipi) : '-',
      sorter: (a, b) => (a.anlasma_fiyati || 0) - (b.anlasma_fiyati || 0),
    },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 50 : 100,
      render: (_, record) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Düzenle'}</Button>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Müşteriler</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])} type={filtreAktifMi ? 'primary' : 'default'} ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Müşteri Adı</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Müşteri seçin..." options={musteriAdOptions} value={filtreAdIds} onChange={setFiltreAdIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Durum</label>
              <div style={{ paddingTop: 6 }}>
                <Checkbox checked={pasifleriGoster} onChange={(e) => setPasifleriGoster(e.target.checked)}>Pasifleri de göster</Checkbox>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Fatura Durumu</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Seçin..." options={faturaliFilterOptions} value={filtreFaturali} onChange={setFiltreFaturali} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Ödeme Rutini</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Rutin seçin..." options={odemeRutiniFilterOptions} value={filtreOdemeRutiniIds} onChange={setFiltreOdemeRutiniIds} maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Paketleme</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Seçin..." options={paketlemeTipiOptions} value={filtrePaketleme} onChange={setFiltrePaketleme} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Anlaşma Fiyatı</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreFiyatOp} onChange={setFiltreFiyatOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Fiyat" value={filtreFiyatDeger} onChange={setFiltreFiyatDeger} min={0} />
              </Space.Compact>
            </Col>
          </Row>
        </Card>
      )}]} />

      {filtreAktifMi && (
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}><Statistic title="Müşteri Sayısı" value={toplamlar.adet} /></Col>
            <Col xs={12} sm={8} md={6}><Statistic title="Haftalık Toplam Kişi" value={toplamlar.toplamKisi} /></Col>
          </Row>
        </Card>
      )}

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{ defaultPageSize: 20, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} müşteri` }}
      />

      <Modal
        title={editingRecord ? `${editingRecord.ad} - Düzenle` : 'Müşteri Düzenle'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <Form.Item name="aktif" label="Durum" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
            <Form.Item name="faturali" label="Fatura" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="Faturalı" unCheckedChildren="Faturasız" />
            </Form.Item>
          </Space>

          <Divider orientation="left" style={{ marginTop: 0 }}>Ödeme & Fiyat</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="odeme_rutini_id" label="Ödeme Rutini">
              <Select allowClear placeholder="Seçin" options={odemeRutinleri.map(o => ({ value: o.id, label: o.ad }))} />
            </Form.Item>
            <Form.Item label="Anlaşma Fiyatı / İndirim">
              <Space>
                <span style={{ fontWeight: 600 }}>{editingRecord?.anlasma_fiyati != null ? anlasmaGoster(editingRecord.anlasma_fiyati, editingRecord.anlasma_fiyat_tipi) : '—'}</span>
                <Button size="small" icon={<HistoryOutlined />} onClick={() => editingRecord && tarihceAc(editingRecord)}>
                  Tarihçe Yönet
                </Button>
              </Space>
            </Form.Item>
            <Form.Item name="paketleme_tipi_id" label="Paketleme" initialValue={2}>
              <Select options={paketlemeTipiOptions} />
            </Form.Item>
          </div>

          <Collapse
            ghost
            items={[{
              key: 'firma',
              label: 'Firma Bilgileri',
              children: (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <Form.Item name="unvan" label="Unvan">
                      <Input />
                    </Form.Item>
                    <Form.Item name="tc_vkn" label="TC / VKN">
                      <Input />
                    </Form.Item>
                    <Form.Item name="vergi_dairesi" label="Vergi Dairesi">
                      <Input />
                    </Form.Item>
                    <Form.Item name="tic_sic_no" label="Tic. Sic. No">
                      <Input />
                    </Form.Item>
                    <Form.Item name="yetkili_isim" label="Yetkili İsim">
                      <Input />
                    </Form.Item>
                    <Form.Item name="yetkili_telefon" label="Yetkili Telefon">
                      <Input />
                    </Form.Item>
                  </div>
                  <Form.Item name="musteri_adres" label="Adres">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="aciklama" label="Açıklama">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </>
              )
            }]}
            style={{ marginBottom: 8 }}
          />

          <Divider orientation="left">Günlük Kişi Sayıları</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {gunler.map(g => (
              <Form.Item key={g.key} name={g.key} label={g.label} style={{ marginBottom: 8 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ))}
          </div>
        </Form>
      </Modal>

      {/* Anlaşma Fiyatı Tarihçesi Modalı */}
      <Modal
        title={tarihceMusteri ? `${tarihceMusteri.ad} — Anlaşma Fiyatı Tarihçesi` : 'Anlaşma Fiyatı Tarihçesi'}
        open={tarihceModalOpen}
        onCancel={() => { setTarihceModalOpen(false); setTarihceMusteri(null); setTarihceData([]); setTarihceEditId(null); tarihceForm.resetFields(); }}
        footer={<Button onClick={() => { setTarihceModalOpen(false); setTarihceMusteri(null); setTarihceData([]); setTarihceEditId(null); tarihceForm.resetFields(); }}>Kapat</Button>}
        width={620}
      >
        <Card size="small" style={{ marginBottom: 12 }} title={tarihceEditId ? 'Satırı Düzenle' : 'Yeni Tarihçe Satırı'}>
          <Form form={tarihceForm} layout="inline" onFinish={tarihceKaydet} initialValues={{ fiyat_tipi: 'sabit' }}>
            <Form.Item name="baslangic_tarihi" label="Başlangıç" rules={[{ required: true, message: 'Tarih zorunlu' }]}>
              <DatePicker format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item name="fiyat_tipi" label="Fiyat Tipi" rules={[{ required: true }]}>
              <Select style={{ width: 180 }} options={FIYAT_TIPI_OPTIONS} />
            </Form.Item>
            <Form.Item
              name="fiyat"
              label={tarihceFiyatTipi === 'tutar_indirim' ? 'İndirim (₺)' : tarihceFiyatTipi === 'yuzde_indirim' ? 'İndirim (%)' : 'Fiyat (₺)'}
              rules={[
                { required: true, message: 'Değer zorunlu' },
                { type: 'number', min: 0, message: 'Negatif olamaz' },
                ...(tarihceFiyatTipi === 'yuzde_indirim' ? [{ type: 'number', max: 100, message: '0-100 arası' }] : []),
              ]}
            >
              {tarihceFiyatTipi === 'yuzde_indirim'
                ? <InputNumber min={0} max={100} step={1} precision={2} style={{ width: 140 }} addonAfter="%" />
                : <InputNumber {...paraInputProps} min={0} style={{ width: 140 }} />}
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} htmlType="submit">
                  {tarihceEditId ? 'Güncelle' : 'Ekle'}
                </Button>
                {tarihceEditId && (
                  <Button onClick={() => { setTarihceEditId(null); tarihceForm.resetFields(); }}>İptal</Button>
                )}
              </Space>
            </Form.Item>
          </Form>
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Bu tarihten itibaren bu fiyat geçerli sayılır. Bir sonraki tarihçe satırına kadar uygulanır.
          </div>
        </Card>

        <Table
          size="small"
          loading={tarihceLoading}
          dataSource={tarihceData}
          rowKey="id"
          pagination={false}
          columns={[
            {
              title: 'Başlangıç', dataIndex: 'baslangic_tarihi', key: 'baslangic_tarihi',
              render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-',
            },
            {
              title: 'Tip', dataIndex: 'fiyat_tipi', key: 'fiyat_tipi',
              render: (v) => {
                const t = v || 'sabit';
                const renk = t === 'sabit' ? 'blue' : t === 'tutar_indirim' ? 'orange' : 'purple';
                const ad = (FIYAT_TIPI_OPTIONS.find(o => o.value === t) || {}).label || t;
                return <Tag color={renk}>{ad}</Tag>;
              },
            },
            {
              title: 'Değer', dataIndex: 'fiyat', key: 'fiyat', align: 'right',
              render: (v, r) => anlasmaGoster(v, r.fiyat_tipi),
            },
            {
              title: 'İşlem', key: 'islem', width: 130, align: 'right',
              render: (_, r) => (
                <Space size={4}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => tarihceDuzenleyiBasla(r)} />
                  <Popconfirm title="Bu satırı silmek istediğinize emin misiniz?" onConfirm={() => tarihceSil(r.id)} okText="Evet" cancelText="Hayır">
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}

export default Musteriler;
